import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const Tabs = TabsPrimitive.Root

// ---- TabsList ---------------------------------------------------------------

const tabsListVariants = cva("text-muted-foreground", {
  variants: {
    variant: {
      // Default (pill): unchanged from prior shadcn-style. Every existing consumer
      // gets this and renders identically.
      pill: "inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1",
      // Underline: enterprise-grade horizontal navigation bar. Sits in its own
      // surface (card-like) with a clean bottom border. Never wraps — overflow scrolls.
      underline: cn(
        "relative flex h-12 items-center justify-start gap-0.5 px-1",
        "border-b border-border/60 bg-card/40 rounded-t-xl",
        "overflow-x-auto overflow-y-hidden whitespace-nowrap no-scrollbar",
        // Subtle fade on overflow edges so users see there's more to scroll
        "[mask-image:linear-gradient(to_right,transparent,black_24px,black_calc(100%-24px),transparent)]"
      ),
    },
  },
  defaultVariants: { variant: "pill" },
})

interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(tabsListVariants({ variant }), className)}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

// ---- TabsTrigger ------------------------------------------------------------

const tabsTriggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        pill:
          "rounded-md px-3 py-1 text-sm data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
        underline: cn(
          // Sizing & spacing — bigger, more breathing room
          "relative h-12 px-4 text-sm gap-2",
          // Resting state — muted text, transparent indicator
          "text-muted-foreground/80 rounded-md",
          // Hover — subtle tint + nudge text toward primary
          "hover:text-foreground hover:bg-muted/60",
          // Active — primary color, semibold, animated indicator
          "data-[state=active]:text-primary data-[state=active]:font-semibold data-[state=active]:bg-primary/[0.06]",
          // Animated indicator bar (pseudo element)
          "after:absolute after:bottom-[-1px] after:start-3 after:end-3 after:h-[3px] after:rounded-t-full after:bg-primary",
          "after:scale-x-0 after:origin-center after:transition-transform after:duration-200",
          "data-[state=active]:after:scale-x-100",
          "[&_svg]:size-4 [&_svg]:shrink-0"
        ),
      },
    },
    defaultVariants: { variant: "pill" },
  }
)

interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(tabsTriggerVariants({ variant }), className)}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

// ---- TabsContent ------------------------------------------------------------

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
